import type {
  AssignDocumentToWorkspaceFolderInput,
  CreateWorkspaceFolderInput,
  GraphQLQuery,
  UpdateWorkspaceFolderPolicyInput,
  WorkspaceDocumentFolderType,
  WorkspaceFolderType,
} from '@affine/graphql';

export type WorkspaceFolder = Pick<
  WorkspaceFolderType,
  | 'accessPreset'
  | 'createdAt'
  | 'id'
  | 'ownerUserId'
  | 'parentFolderId'
  | 'updatedAt'
  | 'workspaceId'
> & {
  // Task 9 adds these server-guarded fields ahead of generated client types.
  // Keep the compatibility boundary local until GraphQL codegen catches up.
  name: string;
  path: string[];
};

export type WorkspaceDocumentFolderAssignment = Pick<
  WorkspaceDocumentFolderType,
  'createdAt' | 'docId' | 'folderId' | 'updatedAt' | 'workspaceId'
>;

export type WorkspaceFolderDocumentCandidate = {
  workspaceId: string;
  docId: string;
  title: string;
  displayContext: string;
};

export type WorkspaceFoldersResponse = {
  workspaceFolders: WorkspaceFolder[];
};

export type CreateWorkspaceFolderResponse = {
  createWorkspaceFolder: WorkspaceFolder;
};

export type UpdateWorkspaceFolderPolicyResponse = {
  updateWorkspaceFolderPolicy: WorkspaceFolder;
};

export type AssignDocumentToWorkspaceFolderResponse = {
  assignDocumentToWorkspaceFolder: WorkspaceDocumentFolderAssignment;
};

export type WorkspaceFolderDocumentCandidatesResponse = {
  workspaceFolderDocumentCandidates: WorkspaceFolderDocumentCandidate[];
};

// The schema types are generated, but these operation descriptors are not yet
// emitted by GraphQL codegen. Keep this cast boundary local to the feature.
export type WorkspaceFolderGql = <Response>(input: {
  query: GraphQLQuery;
  variables: Record<string, unknown>;
}) => Promise<Response>;

export const workspaceFoldersQuery = {
  id: 'workspaceFoldersQuery',
  op: 'workspaceFolders',
  query: `query workspaceFoldersQuery($workspaceId: String!) {
  workspaceFolders(workspaceId: $workspaceId) {
    workspaceId
    id
    parentFolderId
    name
    path
    ownerUserId
    accessPreset
    createdAt
    updatedAt
  }
}`,
} satisfies GraphQLQuery;

export const createWorkspaceFolderMutation = {
  id: 'createWorkspaceFolderMutation',
  op: 'createWorkspaceFolder',
  query: `mutation createWorkspaceFolderMutation($input: CreateWorkspaceFolderInput!) {
  createWorkspaceFolder(input: $input) {
    workspaceId
    id
    parentFolderId
    name
    path
    ownerUserId
    accessPreset
    createdAt
    updatedAt
  }
}`,
} satisfies GraphQLQuery;

export const updateWorkspaceFolderPolicyMutation = {
  id: 'updateWorkspaceFolderPolicyMutation',
  op: 'updateWorkspaceFolderPolicy',
  query: `mutation updateWorkspaceFolderPolicyMutation($input: UpdateWorkspaceFolderPolicyInput!) {
  updateWorkspaceFolderPolicy(input: $input) {
    workspaceId
    id
    parentFolderId
    name
    path
    ownerUserId
    accessPreset
    createdAt
    updatedAt
  }
}`,
} satisfies GraphQLQuery;

export const workspaceFolderDocumentCandidatesQuery = {
  id: 'workspaceFolderDocumentCandidatesQuery',
  op: 'workspaceFolderDocumentCandidates',
  query: `query workspaceFolderDocumentCandidatesQuery($workspaceId: String!) {
  workspaceFolderDocumentCandidates(workspaceId: $workspaceId) {
    workspaceId
    docId
    title
    displayContext
  }
}`,
} satisfies GraphQLQuery;

export const assignDocumentToWorkspaceFolderMutation = {
  id: 'assignDocumentToWorkspaceFolderMutation',
  op: 'assignDocumentToWorkspaceFolder',
  query: `mutation assignDocumentToWorkspaceFolderMutation($input: AssignDocumentToWorkspaceFolderInput!) {
  assignDocumentToWorkspaceFolder(input: $input) {
    workspaceId
    docId
    folderId
    createdAt
    updatedAt
  }
}`,
} satisfies GraphQLQuery;

export type CreateFolderInput = CreateWorkspaceFolderInput;
export type UpdateFolderPolicyInput = UpdateWorkspaceFolderPolicyInput;
export type AssignDocumentInput = AssignDocumentToWorkspaceFolderInput;
